import React from 'react';
import { Box, Checkbox, Switch, FormControlLabel, Typography, CircularProgress, Chip } from '@mui/material';
import { Features } from '@gql/base-types';
import { useGetPortalFeaturesQuery } from '../features/userManagement/queries/getPortalFeatures.query.generated';
import { filterEligibleFeatures } from '@features/userManagement/utils/featureEligibility';

interface FeatureNode {
  feature: Features;
  children: FeatureNode[];
}

interface FeatureTreeChecklistProps {
  selectedFeatureIds: Set<number>;
  onChange: (featureIds: Set<number>) => void;
  disabled?: boolean;
  /** 'checkbox' (default) or 'switch' for toggle-style controls */
  variant?: 'checkbox' | 'switch';
  /** When provided, hide features whose config.tiers don't overlap with these tiers */
  targetUserTiers?: Set<string>;
  /** When provided, hide features whose config.requires service isn't enabled on a rooftop */
  enabledServices?: Set<string>;
}

/**
 * Shared reusable feature tree checklist.
 * Queries features WHERE application = 'portal', builds hierarchy from category_id,
 * and enforces parent-child rules:
 * - Selecting a child auto-selects the parent
 * - Deselecting a parent deselects all children
 * - Siblings are independent
 */
const FeatureTreeChecklist: React.FC<FeatureTreeChecklistProps> = ({
  selectedFeatureIds,
  onChange,
  disabled = false,
  variant = 'checkbox',
  targetUserTiers,
  enabledServices,
}) => {
  const { data, loading } = useGetPortalFeaturesQuery();

  const allFeatures = (data?.features ?? []) as Features[];

  // Tier + service-requirement gating — single source of truth in featureEligibility.
  const features = React.useMemo(
    () => filterEligibleFeatures(allFeatures, { tiers: targetUserTiers, enabledServices }),
    [allFeatures, targetUserTiers, enabledServices],
  );

  // Show "Lite" badge only when user has mixed tiers
  const showLiteBadge = targetUserTiers && targetUserTiers.size > 1;

  // Build tree from flat list using category_id
  const tree = React.useMemo(() => {
    const nodeMap = new Map<number, FeatureNode>();
    const roots: FeatureNode[] = [];

    // Create nodes
    for (const feature of features) {
      nodeMap.set(feature.id, { feature, children: [] });
    }

    // Build hierarchy
    for (const feature of features) {
      const node = nodeMap.get(feature.id)!;
      if (feature.category_id && nodeMap.has(feature.category_id)) {
        nodeMap.get(feature.category_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }, [features]);

  // Get all descendant IDs for a feature
  const getDescendantIds = React.useCallback(
    (featureId: number): number[] => {
      const result: number[] = [];
      const nodeMap = new Map<number, FeatureNode>();
      const buildMap = (nodes: FeatureNode[]) => {
        for (const node of nodes) {
          nodeMap.set(node.feature.id, node);
          buildMap(node.children);
        }
      };
      buildMap(tree);

      const collect = (id: number) => {
        const node = nodeMap.get(id);
        if (!node) return;
        for (const child of node.children) {
          result.push(child.feature.id);
          collect(child.feature.id);
        }
      };
      collect(featureId);
      return result;
    },
    [tree]
  );

  // Get all ancestor IDs for a feature
  const getAncestorIds = React.useCallback(
    (featureId: number): number[] => {
      const result: number[] = [];
      const parentMap = new Map<number, number>();
      for (const feature of features) {
        if (feature.category_id) {
          parentMap.set(feature.id, feature.category_id);
        }
      }

      let current = featureId;
      while (parentMap.has(current)) {
        const parentId = parentMap.get(current)!;
        result.push(parentId);
        current = parentId;
      }
      return result;
    },
    [features]
  );

  const handleToggle = (featureId: number) => {
    const newSelected = new Set(selectedFeatureIds);

    if (newSelected.has(featureId)) {
      // Deselecting: remove this feature and all descendants
      newSelected.delete(featureId);
      for (const descendantId of getDescendantIds(featureId)) {
        newSelected.delete(descendantId);
      }
    } else {
      // Selecting: add this feature and all ancestors
      newSelected.add(featureId);
      for (const ancestorId of getAncestorIds(featureId)) {
        newSelected.add(ancestorId);
      }
    }

    onChange(newSelected);
  };

  const renderNode = (node: FeatureNode, depth: number = 0) => {
    const isSelected = selectedFeatureIds.has(node.feature.id);
    const ControlComponent = variant === 'switch' ? Switch : Checkbox;

    return (
      <Box key={node.feature.id} sx={{ ml: depth * 3 }}>
        <FormControlLabel
          control={
            <ControlComponent
              checked={isSelected}
              onChange={() => handleToggle(node.feature.id)}
              disabled={disabled}
              size="small"
            />
          }
          label={
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  {node.feature.label}
                </Typography>
                {showLiteBadge && (node.feature.config as { tiers?: string[] } | null)?.tiers?.length && (
                  <Chip label="Lite" size="small" color="info"
                    sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                )}
              </Box>
              {node.feature.description && (
                <Typography variant="caption" color="text.secondary">
                  {node.feature.description}
                </Typography>
              )}
            </Box>
          }
          sx={{ alignItems: 'flex-start', mb: 0.5 }}
        />
        {node.children.map(child => renderNode(child, depth + 1))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (features.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No features available.
      </Typography>
    );
  }

  return <Box>{tree.map(node => renderNode(node))}</Box>;
};

export default FeatureTreeChecklist;
